import { z } from 'zod';
import { DiscountTypeSchema, PricingTypeEnum } from './pricing-rule';

// Time window schema for pricing calculations
export const timeWindowSchema = z.object({
  day: z.number(),
  start: z.number(),
  end: z.number(),
});

export const pricingCalculateSchema = z.object({
  cartId: z.string(),
});

export const customerPricingModelEnum = z.enum([
  PricingTypeEnum.BUNDLE,
  PricingTypeEnum.FIXED,
  PricingTypeEnum.PAY_AS_YOU_GO,
  PricingTypeEnum.STANDARD,
]);

// Pricing quote input schema
export const pricingQuoteSchema = z.object({
  pricingModel: customerPricingModelEnum,
  containerType: z.string().min(1, 'Container type is required'),
  materialId: z.string().min(1, 'Material ID is required'),
  rentalDuration: z.object({
    dropOffWindow: timeWindowSchema,
    pickupWindow: timeWindowSchema.optional(),
  }),
  location: z.object({
    zipCode: z.string().min(5).max(10),
  }),
  selectedRuleId: z.string().min(1, 'Pricing rule ID is required'),
  customerId: z.string().optional(),
  addOns: z.array(z.string()).optional(),
});

// Pricing option response schema
export const pricingOptionSchema = z.object({
  ruleIds: z.array(z.string()), // Updated to support multiple rule IDs
  model: customerPricingModelEnum,
  available: z.boolean(),
  displayName: z.string(),
  description: z.string(),
  materialName: z.string().optional(), // Material name for Single Material pricing disclaimer
  pricing: z.object({
    baseFee: z.number().optional(),
    includedDays: z.number().optional(),
    includedTons: z.number().optional(),
    dayOverageRate: z.number().optional(),
    tonOverageRate: z.number().optional(),
    flatRate: z.number().optional(),
    cleanLoadDays: z.number().optional(),
    dailyOverageFee: z.number().optional(),
    perTonRate: z.number().optional(),
    perDayRate: z.number().optional(),
    progressiveDiscounts: z
      .array(
        z.object({
          threshold: z.number(),
          type: z.enum(['days', 'tons']),
          discountPercent: z.number(),
        }),
      )
      .optional(),
  }),
  estimatedTotal: z.number(),
  originalTotal: z.number().optional(), // Price before discount
  discountInfo: z
    .object({
      type: DiscountTypeSchema,
      value: z.number(), // Percentage value (e.g., 20 for 20%) or flat amount in cents
    })
    .optional(),
});

// Pricing calculate response schema
export const pricingCalculateResponseSchema = z.object({
  options: z.array(pricingOptionSchema),
  metadata: z.object({
    rentalDays: z.number(),
    estimatedWeight: z.number(),
    location: z.string(),
  }),
});

// Quote response schema
export const quoteResponseSchema = z.object({
  quoteId: z.string(),
  expiresAt: z.date(),
  quote: z.object({
    model: customerPricingModelEnum,
    charges: z.array(
      z.object({
        type: z.enum(['base', 'overage', 'discount', 'material', 'addon']),
        description: z.string(),
        amount: z.number(),
      }),
    ),
    subtotal: z.number(),
    discounts: z.number(),
    taxes: z.number(),
    total: z.number(),
    terms: z.object({
      rentalPeriod: z.object({
        start: z.date(),
        end: z.date(),
        days: z.number(),
      }),
      includedItems: z
        .object({
          days: z.number().optional(),
          tons: z.number().optional(),
        })
        .optional(),
      overageRates: z
        .object({
          perDay: z.number().optional(),
          perTon: z.number().optional(),
        })
        .optional(),
    }),
  }),
});

// Quote validation response schema
export const quoteValidationResponseSchema = z.object({
  valid: z.boolean(),
  error: z.string().optional(),
  quote: z.any().optional(),
});

// Input types for TypeScript
export type PricingCalculateInput = z.infer<typeof pricingCalculateSchema>;
export type PricingQuoteInput = z.infer<typeof pricingQuoteSchema>;
export type TimeWindow = z.infer<typeof timeWindowSchema>;

// Response types for TypeScript
export type PricingOption = z.infer<typeof pricingOptionSchema>;
export type PricingCalculateResponse = z.infer<typeof pricingCalculateResponseSchema>;
export type QuoteResponse = z.infer<typeof quoteResponseSchema>;
export type QuoteValidationResponse = z.infer<typeof quoteValidationResponseSchema>;
